import {React, classNames, useCallback, useLocalStore, useAsObservableSource, Popover2} from '@api'
import {Button, Icon, Menu, MenuDivider, MenuItem, Popover} from '@blueprintjs/core'
import {Tooltip2} from '@api'
import {Tooltip} from '@material-ui/core'
import {BorderAll, BorderClear, BorderHorizontal, BorderVertical, BurstModeOutlined, Filter1, ImageOutlined, Layers, PanoramaOutlined, ViewComfy} from '@material-ui/icons'
import {CardViewToolbarMenuPanelStoreContext, useCardViewToolbarMenuPanelStore} from '@modules/catalog'
import {ImageSourcesMenuContent} from '@modules/contextMenus'
import * as builderCardCss from '@modules/contextMenus/menus/BuilderCardContextMenu.css'
import {useStores, useVipLocalStore, VipLocalStore} from '@modules/VipLocalStore'
import {CatalogImageSourceConfig, StoresInstance, useCatalog} from '@stores'
import {observer} from '@api'
import {Chip, PrimaryIconButton} from 'centric-ui-components'
import {action, autorun, observable, runInAction, trace} from 'mobx'
import {IDisposer} from 'mobx-utils'
import {Fragment, useContext, useEffect, useState} from 'react'
import * as css from './ImageSourceToolbarButton.css'

/// region Store

export class ImageSourceToolbarButtonStore extends VipLocalStore {
  constructor(stores: StoresInstance) {
    super(stores)
  }

  registerEffects = () => {
    const toDispose: Array<IDisposer> = [
      autorun(() => {
        const {catalog: {settings: {imageSource: {ids}}}} = this
        runInAction(() => {
          this.localSelectedImageSourceIds.replace(ids)
        })
      }),

      autorun(() => {
        const {localSelectedImageSourceIds} = this,
          size = localSelectedImageSourceIds.size

        runInAction(() => {
          const {catalog: {settings: {imageSource: {ids}}}} = this

          if (!ids) return // can happen while changing workspaces
          if (size != ids.length || !ids.every(id => localSelectedImageSourceIds.has(id))) {
            this._debounceUpdateImageSourceIds(Array.from(localSelectedImageSourceIds.keys()))
          }
        })
      }, {name: `Overwrite catalog image source with selected choices from toolbar menu`})
    ]

    return () => toDispose.forEach(d => d())
  }

  _debounceUpdateImageSourceIds = _.debounce((ids: string[]) => {
    this.catalog.settings.imageSource.ids = ids
  }, 25)

  localSelectedImageSourceIds = observable.set<string>([], {name: 'localSelectedImageSourceIds'})
}

export const ImageSourceToolbarButtonStoreContext = React.createContext<ImageSourceToolbarButtonStore>(null)
export const useImageSourceToolbarButtonStore = () => React.useContext(ImageSourceToolbarButtonStoreContext)

/// endregion

/// region Components

interface MyProps {
  className?: string
}

export const ImageSourceToolbarButton = observer<MyProps>(function ImageSourceToolbarButton({className, ...props}) {
  const store = useVipLocalStore((stores) => new ImageSourceToolbarButtonStore(stores)),
    {strings, catalog: {imageSourceOptions, imageSourceById, imageSource}} = store,
    isInViewStore = useContext(CardViewToolbarMenuPanelStoreContext) != null

  useEffect(() => store.registerEffects(), [])

  return (
    <ImageSourceToolbarButtonStoreContext.Provider value={store}>
      {/*<NavbarHeading>Image Source:</NavbarHeading>*/}
      <Popover2
        interactionKind={'hover'} //isInViewStore ? 'hover' : 'click'}
        position={'top-left'} // isInViewStore ? 'right-top' : 'bottom-left'}
        minimal
        //disabled={viewMode === 'table' || imageSourceOptions.length <= 1}
        // usePortal={true}
        className={className}
        content={<Menu className={css.menu}>
          <ImageSourcesMenuContent/>
        </Menu>}>
        <Tooltip2 content={'Image Sources'} placement='right'>
          <Button
            className={css.imageSourceButton}
            icon={imageSource?.view === 'first' ? <PanoramaOutlined/> : <BurstModeOutlined/>}
            rightIcon={
              isInViewStore ? 'caret-right' : 'caret-down'
            }
            minimal
            text={_.isEmpty(imageSource.ids) ? strings.all : imageSource.ids.map(id => imageSourceById[id]?.label ?? strings.emptyLabel).join(', ')}  // selectedImageSourceFields.map(f => labelForFieldName_currentCatalog(imageSourceById[f].field).join(', ')}
          />
        </Tooltip2>
      </Popover2>
    </ImageSourceToolbarButtonStoreContext.Provider>)
})

/// endregion

const CatalogImageSourceMenuItem = observer<{ imageSource: CatalogImageSourceConfig }>(function CatalogImageSourceMenuItem({imageSource}) {
  const store = useImageSourceToolbarButtonStore()
  if (!store) return null

  const {localSelectedImageSourceIds, catalog} = store,
    {id, field, label, index, workspace} = imageSource,
    isSelected = _.isEmpty(localSelectedImageSourceIds) || localSelectedImageSourceIds?.has(id)

  let menuLabel = catalog.sectionConfigForField(id)?.label
  if (menuLabel) {
    const m = menuLabel.match(/\((.*)\)/)
    menuLabel = _.last(m)
  }

  // console.log({...imageSource, menuLabel})
  return (
    <MenuItem
      icon={!isSelected ? 'blank' : 'tick'}
      className={css.imageSourceMenuItem}
      data-id={id}
      data-field={field}
      //active={id === }
      text={label}
      shouldDismissPopover={false}
      labelElement={menuLabel ? <Chip className={css.sectionLabel} label={menuLabel}/> : undefined}
      onClick={() => {
        if (isSelected) {
          localSelectedImageSourceIds?.delete(id)
        } else {
          localSelectedImageSourceIds?.add(id)
        }
      }}
    >
    </MenuItem>)
})

export const CatalogImageSourceMenuItems = observer(function CatalogImageSourceMenuItems() {
  const {
    imageSourceOptions,
    settings: {viewMode, imageSource: {view}, borders, borders: {cardBorders, gridLines}}
  } = useCatalog()

  return <Fragment>
    {imageSourceOptions?.map((imageSource, i) => <CatalogImageSourceMenuItem imageSource={imageSource} key={i}/>)}
    {viewMode === 'card' && <Fragment>
		<MenuDivider/>
		<MenuItem shouldDismissPopover={false} icon={cardBorders === true ? 'tick' : 'blank'} text='Borders on Cards' onClick={() => borders.cardBorders = cardBorders !== true}/>
	</Fragment>}

    {false && <Fragment>
		<MenuDivider title='Grid Lines'/>
		<MenuItem shouldDismissPopover={false} labelElement={<BorderClear/>} text='None' icon={gridLines === 'none' ? 'tick' : 'blank'} onClick={() => borders.gridLines = 'none'}/>
		<MenuItem shouldDismissPopover={false} labelElement={<BorderHorizontal/>} text='Horizontal' icon={gridLines === 'horizontal' ? 'tick' : 'blank'} onClick={() => borders.gridLines = 'horizontal'}/>
		<MenuItem shouldDismissPopover={false} labelElement={<BorderVertical/>} text='Vertical' icon={gridLines === 'vertical' ? 'tick' : 'blank'} onClick={() => borders.gridLines = 'vertical'}/>
		<MenuItem shouldDismissPopover={false} labelElement={<BorderAll/>} text='Both' icon={gridLines === 'both' ? 'tick' : 'blank'} onClick={() => borders.gridLines = 'both'}/>
	</Fragment>}
  </Fragment>
})


// WEBPACK FOOTER //
// ./src/modules/catalog/internal/topBar/components/ImageSourceToolbarButton.tsx